import {
  type IActivityJob,
  type IDelayRegularMetadata,
  type IDigestRegularMetadata,
  IDigestTimedMetadata,
  JobStatusEnum,
  StepTypeEnum,
} from '@novu/shared';
import { format } from 'date-fns';
import { ChevronDown, Info, Route } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/primitives/badge';
import { Button } from '@/components/primitives/button';
import { cn } from '@/utils/ui';
import { STEP_TYPE_TO_COLOR } from '../../utils/color';
import { formatJSONString } from '../../utils/string';
import { STEP_TYPE_TO_ICON } from '../icons/utils';
import { Card, CardContent, CardHeader } from '../primitives/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../primitives/tooltip';
import { TimeDisplayHoverCard } from '../time-display-hover-card';
import TruncatedText from '../truncated-text';
import { JOB_STATUS_CONFIG } from './constants';
import { ExecutionDetailItem } from './execution-detail-item';

interface ActivityJobItemProps {
  job: IActivityJob;
  isFirst: boolean;
  isLast: boolean;
}

export function ActivityJobItem({ job, isFirst, isLast }: ActivityJobItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="relative flex items-center gap-1">
      <div
        className={cn(
          'absolute left-[11px] h-[calc(100%+24px)] w-[1px] bg-neutral-200',
          isFirst ? 'top-[50%]' : 'top-0',
          isLast ? 'h-[50%]' : 'h-[calc(100%+24px)]',
          isFirst && isLast && 'bg-transparent'
        )}
      />

      <JobStatusIndicator status={job.status} />

      <Card className="border-1 flex-1 overflow-hidden border border-neutral-200 p-1 shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]">
        <CardHeader
          className="flex flex-row items-center justify-between bg-white p-2 px-1 hover:cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-1.5">
            <div
              className={`h-5 w-5 rounded-full border opacity-40 border-${STEP_TYPE_TO_COLOR[job.type as keyof typeof STEP_TYPE_TO_COLOR]}`}
            >
              <div
                className={`h-full w-full rounded-full bg-neutral-50 text-${STEP_TYPE_TO_COLOR[job.type as keyof typeof STEP_TYPE_TO_COLOR]} flex items-center justify-center`}
              >
                {getJobIcon(job.type)}
              </div>
            </div>
            <span className="text-foreground-950 text-xs capitalize">{job?.step?.name || formatJobType(job.type)}</span>
          </div>

          <Button
            variant="secondary"
            mode="ghost"
            size="xs"
            className="text-foreground-600 !mt-0 h-5 gap-0 p-0 leading-[12px] hover:bg-transparent"
          >
            Show more
            <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
          </Button>
        </CardHeader>

        {!isExpanded && (
          <CardContent className="rounded-lg bg-neutral-50 p-2">
            <div className="flex items-center justify-between">
              <TruncatedText className="text-foreground-400 max-w-[300px] pr-2 text-xs">
                {getStatusMessage(job)}
              </TruncatedText>
              <Badge variant="lighter" color="gray" size="sm" className="whitespace-nowrap">
                <TimeDisplayHoverCard date={new Date(job.updatedAt)}>
                  {format(new Date(job.updatedAt), 'MMM d yyyy, HH:mm:ss')}
                </TimeDisplayHoverCard>
              </Badge>
            </div>
          </CardContent>
        )}

        {isExpanded && <JobDetails job={job} />}
      </Card>
    </div>
  );
}

function formatJobType(type?: StepTypeEnum): string {
  return type?.replace(/_/g, ' ') || '';
}

function getStatusMessage(job: IActivityJob): string | React.ReactNode {
  if (job.status === JobStatusEnum.MERGED) {
    return 'Step merged with another execution';
  }

  if (job.status === JobStatusEnum.PENDING) {
    return 'Job is pending';
  }

  if (job.status === JobStatusEnum.SKIPPED) {
    return 'Step was skipped';
  }

  if (
    (job.status === JobStatusEnum.FAILED || job.status === JobStatusEnum.CANCELED) &&
    job.executionDetails?.length > 0
  ) {
    const lastExecutionDetail = job.executionDetails[job.executionDetails.length - 1];

    return lastExecutionDetail ? (
      <div className="flex items-center gap-2">
        {lastExecutionDetail.raw ? (
          <TraceTooltip message={lastExecutionDetail.detail} raw={lastExecutionDetail.raw} variant="info" />
        ) : (
          <span className={job.status === JobStatusEnum.FAILED ? 'text-destructive' : 'text-text-soft'}>
            <TruncatedText>{lastExecutionDetail.detail}</TruncatedText>
          </span>
        )}
      </div>
    ) : job.status === JobStatusEnum.FAILED ? (
      'Step execution failed'
    ) : (
      'Step was skipped'
    );
  }

  switch (job.type?.toLowerCase()) {
    case StepTypeEnum.TRIGGER:
      if (job.status === JobStatusEnum.COMPLETED) {
        return 'Step completed';
      }

      return '';

    case StepTypeEnum.THROTTLE:
      if (job.status === JobStatusEnum.COMPLETED) {
        return 'Throttle step completed';
      }

      return '';
    case StepTypeEnum.DIGEST:
      if (job.status === JobStatusEnum.COMPLETED) {
        if ((job.digest as IDigestTimedMetadata).timed?.untilDate) {
          return `Digested events until scheduled time${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
        }

        return `Digested ${job.digest?.events?.length ?? 0} events for ${(job.digest as IDigestRegularMetadata)?.amount ?? 0} ${
          (job.digest as IDigestRegularMetadata)?.unit ?? ''
        }${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
      }

      if (job.status === JobStatusEnum.DELAYED) {
        const untilDate = (job.digest as IDigestTimedMetadata).timed?.untilDate;
        if (untilDate) {
          const untilDateFormatted = format(new Date(untilDate), 'MMM d yyyy, HH:mm:ss');
          return `Collecting events until ${untilDateFormatted}${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
        }

        return job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0
          ? 'Extended to subscriber schedule'
          : `Collecting Digest events for ${(job.digest as IDigestRegularMetadata)?.amount ?? 0} ${
              (job.digest as IDigestRegularMetadata)?.unit ?? ''
            }`;
      }

      return '';
    case StepTypeEnum.DELAY: {
      const { unit, amount } = (job.digest || {}) as IDelayRegularMetadata;

      if (job.status === JobStatusEnum.COMPLETED) {
        if ((job.digest as IDigestTimedMetadata)?.timed?.untilDate) {
          return `Delayed until scheduled time${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
        }
        if (unit && amount) {
          return `Delayed for ${amount} ${unit}${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
        }

        return 'Delay completed';
      }

      if (job.status === JobStatusEnum.DELAYED) {
        let msg = 'Waiting';

        const untilDate = (job.digest as IDigestTimedMetadata)?.timed?.untilDate;
        if (untilDate) {
          const untilDateFormatted = format(new Date(untilDate), 'MMM d yyyy, HH:mm:ss');
          return `Waiting until ${untilDateFormatted}${job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0 ? `, extended to subscriber schedule` : ''}`;
        }

        if (unit && amount) {
          msg =
            job.scheduleExtensionsCount && job.scheduleExtensionsCount > 0
              ? 'Extended to subscriber schedule'
              : `Waiting for ${amount} ${unit}`;
        }

        return msg;
      }

      return '';
    }
    default:
      if (job.status === JobStatusEnum.COMPLETED) {
        return 'Message sent successfully';
      }

      return '';
  }
}

function TraceTooltip({ message, raw, variant = 'error' }: { message: string; raw: any; variant?: 'error' | 'info' }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="flex items-center gap-1 text-left hover:cursor-default">
          <span className={cn('text-destructive', variant === 'error' ? 'text-destructive' : 'text-text-soft')}>
            {message}
          </span>
          <Info className={cn('h-3 w-3 shrink-0', variant === 'error' ? 'text-destructive' : 'text-text-soft')} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[400px] border border-neutral-200 bg-white p-3 shadow-lg">
        <pre className="text-foreground-700 max-h-[300px] w-full overflow-auto rounded bg-neutral-50 p-2 font-mono text-xs">
          {formatJSONString(raw)}
        </pre>
      </TooltipContent>
    </Tooltip>
  );
}

function getJobIcon(type?: StepTypeEnum) {
  const Icon = STEP_TYPE_TO_ICON[type?.toLowerCase() as keyof typeof STEP_TYPE_TO_ICON] ?? Route;

  return <Icon className="h-3.5 w-3.5" />;
}

function getJobClasses(status: JobStatusEnum) {
  switch (status) {
    case JobStatusEnum.COMPLETED:
      return 'text-success';
    case JobStatusEnum.FAILED:
      return 'text-destructive';
    case JobStatusEnum.DELAYED:
      return 'text-warning';
    case JobStatusEnum.MERGED:
      return 'text-neutral-300';
    default:
      return 'text-neutral-300';
  }
}

function JobDetails({ job }: { job: IActivityJob }) {
  return (
    <div className="border-t border-neutral-100 p-4">
      <div className="flex flex-col gap-4">
        {job.executionDetails && job.executionDetails.length > 0 && (
          <div className="flex flex-col gap-2">
            {job.executionDetails.map((detail, index) => (
              <ExecutionDetailItem
                key={index}
                detail={{ ...detail, status: job.executionDetails[job.executionDetails.length - 1].status }}
              />
            ))}
          </div>
        )}
        {/*
        TODO: Missing backend support for digest events widget
        {job.type === 'digest' && job.digest?.events && (
          <ActivityDetailCard title="Digest Events" expandable={true} open>
            <div className="min-w-0 max-w-full font-mono">
              {job.digest.events.map((event: DigestEvent, index: number) => (
                <div key={index} className="group flex items-center gap-2 rounded-sm px-1 py-1.5 hover:bg-neutral-100">
                  <RiCheckboxCircleLine className="text-success h-4 w-4 shrink-0" />
                  <div className="flex items-center gap-2 truncate">
                    <span className="truncate text-xs text-neutral-500">{event.type}</span>
                    <span className="text-xs text-neutral-400">
                      {`${format(new Date(job.updatedAt), 'HH:mm')} UTC`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ActivityDetailCard>
        )} */}
      </div>
    </div>
  );
}

interface JobStatusIndicatorProps {
  status: JobStatusEnum;
}

function JobStatusIndicator({ status }: JobStatusIndicatorProps) {
  const { icon: Icon, animationClass } = JOB_STATUS_CONFIG[status] || JOB_STATUS_CONFIG[JobStatusEnum.PENDING];

  return (
    <div className="relative flex-shrink-0">
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]">
        <div className={`${getJobClasses(status)} flex items-center justify-center`}>
          <Icon className={cn('h-4 w-4', animationClass)} />
        </div>
      </div>
    </div>
  );
}
